// src/utils/indexedDB.ts
export interface TimeEntry {
    id?: number;
    userId: string;
    projectId: string;
    action: 'clock_in' | 'clock_out' | 'break_start' | 'break_end';
    timestamp: string;
    location?: { lat: number; lng: number; address?: string };
    synced: boolean;
    deviceId?: string;
  }
  
  export interface TaskUpdate {
    id?: number;
    taskId: string;
    projectId: string;
    status: string;
    progress?: number;
    notes?: string;
    timestamp: string;
    synced: boolean;
  }
  
  export interface Project {
    id: string;
    name: string;
    description?: string;
    status: string;
    assignedUsers: string[];
    lastUpdated: string;
    cached: boolean;
  }
  
  export interface LocationEntry {
    id?: number;
    userId: string;
    latitude: number;
    longitude: number;
    accuracy?: number;
    timestamp: string;
    address?: string;
    synced: boolean;
  }
  
  export interface OfflineAction {
    id?: number;
    type: 'time_entry' | 'task_update' | 'location' | 'photo_upload' | 'form_submit';
    data: any;
    priority: 'high' | 'medium' | 'low';
    timestamp: string;
    retryCount: number;
    maxRetries: number;
  }
  
  class IndexedDBService {
    private db: IDBDatabase | null = null;
    private readonly dbName = 'StaffluentDB';
    private readonly dbVersion = 1;
  
    async init(): Promise<void> {
      return new Promise((resolve, reject) => {
        const request = indexedDB.open(this.dbName, this.dbVersion);
  
        request.onerror = () => {
          console.error('IndexedDB failed to open');
          reject(request.error);
        };
  
        request.onsuccess = () => {
          this.db = request.result;
          resolve();
        };
  
        request.onupgradeneeded = (event) => {
          const db = (event.target as IDBOpenDBRequest).result;
  
          // Time Entries Store
          if (!db.objectStoreNames.contains('timeEntries')) {
            const timeStore = db.createObjectStore('timeEntries', { keyPath: 'id', autoIncrement: true });
            timeStore.createIndex('userId', 'userId', { unique: false });
            timeStore.createIndex('projectId', 'projectId', { unique: false });
            timeStore.createIndex('timestamp', 'timestamp', { unique: false });
            timeStore.createIndex('synced', 'synced', { unique: false });
          }
  
          // Task Updates Store
          if (!db.objectStoreNames.contains('taskUpdates')) {
            const taskStore = db.createObjectStore('taskUpdates', { keyPath: 'id', autoIncrement: true });
            taskStore.createIndex('taskId', 'taskId', { unique: false });
            taskStore.createIndex('projectId', 'projectId', { unique: false });
            taskStore.createIndex('synced', 'synced', { unique: false });
          }
  
          // Projects Store
          if (!db.objectStoreNames.contains('projects')) {
            const projectStore = db.createObjectStore('projects', { keyPath: 'id' });
            projectStore.createIndex('lastUpdated', 'lastUpdated', { unique: false });
          }
  
          // Locations Store
          if (!db.objectStoreNames.contains('locations')) {
            const locationStore = db.createObjectStore('locations', { keyPath: 'id', autoIncrement: true });
            locationStore.createIndex('userId', 'userId', { unique: false });
            locationStore.createIndex('timestamp', 'timestamp', { unique: false });
            locationStore.createIndex('synced', 'synced', { unique: false });
          }
  
          // Offline Queue Store
          if (!db.objectStoreNames.contains('offlineQueue')) {
            const queueStore = db.createObjectStore('offlineQueue', { keyPath: 'id', autoIncrement: true });
            queueStore.createIndex('priority', 'priority', { unique: false });
            queueStore.createIndex('timestamp', 'timestamp', { unique: false });
            queueStore.createIndex('type', 'type', { unique: false });
          }
        };
      });
    }
  
    // Time Entries Methods
    async addTimeEntry(entry: TimeEntry): Promise<number> {
      if (!this.db) throw new Error('Database not initialized');
  
      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction(['timeEntries'], 'readwrite');
        const store = transaction.objectStore('timeEntries');
        const request = store.add(entry);
  
        request.onsuccess = () => resolve(request.result as number);
        request.onerror = () => reject(request.error);
      });
    }
  
    async getUnsyncedTimeEntries(): Promise<TimeEntry[]> {
      if (!this.db) throw new Error('Database not initialized');
  
      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction(['timeEntries'], 'readonly');
        const store = transaction.objectStore('timeEntries');
        const index = store.index('synced');
        // @ts-ignore
        const request = index.getAll(false);
  
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
    }
  
    async markTimeEntrySynced(id: number): Promise<void> {
      if (!this.db) throw new Error('Database not initialized');
  
      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction(['timeEntries'], 'readwrite');
        const store = transaction.objectStore('timeEntries');
        const getRequest = store.get(id);
  
        getRequest.onsuccess = () => {
          const entry = getRequest.result;
          if (entry) {
            entry.synced = true;
            const updateRequest = store.put(entry);
            updateRequest.onsuccess = () => resolve();
            updateRequest.onerror = () => reject(updateRequest.error);
          } else {
            reject(new Error('Entry not found'));
          }
        };
        getRequest.onerror = () => reject(getRequest.error);
      });
    }
  
    // Task Updates Methods
    async addTaskUpdate(update: TaskUpdate): Promise<number> {
      if (!this.db) throw new Error('Database not initialized');
  
      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction(['taskUpdates'], 'readwrite');
        const store = transaction.objectStore('taskUpdates');
        const request = store.add(update);
  
        request.onsuccess = () => resolve(request.result as number);
        request.onerror = () => reject(request.error);
      });
    }
  
    async getUnsyncedTaskUpdates(): Promise<TaskUpdate[]> {
      if (!this.db) throw new Error('Database not initialized');
  
      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction(['taskUpdates'], 'readonly');
        const store = transaction.objectStore('taskUpdates');
        const index = store.index('synced');
        // @ts-ignore
        const request = index.getAll(false);
  
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
    }
  
    async markTaskUpdateSynced(id: number): Promise<void> {
      if (!this.db) throw new Error('Database not initialized');
  
      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction(['taskUpdates'], 'readwrite');
        const store = transaction.objectStore('taskUpdates');
        const getRequest = store.get(id);
  
        getRequest.onsuccess = () => {
          const update = getRequest.result;
          if (update) {
            update.synced = true;
            const updateRequest = store.put(update);
            updateRequest.onsuccess = () => resolve();
            updateRequest.onerror = () => reject(updateRequest.error);
          } else {
            reject(new Error('Task update not found'));
          }
        };
        getRequest.onerror = () => reject(getRequest.error);
      });
    }
  
    // Projects Methods
    async saveProject(project: Project): Promise<void> {
      if (!this.db) throw new Error('Database not initialized');
  
      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction(['projects'], 'readwrite');
        const store = transaction.objectStore('projects');
        const request = store.put({ ...project, cached: true });
  
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    }
  
    async getProject(id: string): Promise<Project | null> {
      if (!this.db) throw new Error('Database not initialized');
  
      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction(['projects'], 'readonly');
        const store = transaction.objectStore('projects');
        const request = store.get(id);
  
        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => reject(request.error);
      });
    }
  
    async getAllProjects(): Promise<Project[]> {
      if (!this.db) throw new Error('Database not initialized');
  
      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction(['projects'], 'readonly');
        const store = transaction.objectStore('projects');
        const request = store.getAll();
  
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
    }
  
    // Location Methods
    async addLocation(location: LocationEntry): Promise<number> {
      if (!this.db) throw new Error('Database not initialized');
  
      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction(['locations'], 'readwrite');
        const store = transaction.objectStore('locations');
        const request = store.add(location);
  
        request.onsuccess = () => resolve(request.result as number);
        request.onerror = () => reject(request.error);
      });
    }
  
    async getUnsyncedLocations(): Promise<LocationEntry[]> {
      if (!this.db) throw new Error('Database not initialized');
  
      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction(['locations'], 'readonly');
        const store = transaction.objectStore('locations');
        const index = store.index('synced');
        // @ts-ignore
        const request = index.getAll(false);
  
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
    }
  
    async markLocationSynced(id: number): Promise<void> {
      if (!this.db) throw new Error('Database not initialized');
  
      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction(['locations'], 'readwrite');
        const store = transaction.objectStore('locations');
        const getRequest = store.get(id);
  
        getRequest.onsuccess = () => {
          const location = getRequest.result;
          if (location) {
            location.synced = true;
            const updateRequest = store.put(location);
            updateRequest.onsuccess = () => resolve();
            updateRequest.onerror = () => reject(updateRequest.error);
          } else {
            reject(new Error('Location entry not found'));
          }
        };
        getRequest.onerror = () => reject(getRequest.error);
      });
    }
  
    // Offline Queue Methods
    async addToOfflineQueue(action: OfflineAction): Promise<number> {
      if (!this.db) throw new Error('Database not initialized');
  
      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction(['offlineQueue'], 'readwrite');
        const store = transaction.objectStore('offlineQueue');
        const request = store.add(action);
  
        request.onsuccess = () => resolve(request.result as number);
        request.onerror = () => reject(request.error);
      });
    }
  
    async getOfflineQueue(): Promise<OfflineAction[]> {
      if (!this.db) throw new Error('Database not initialized');
  
      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction(['offlineQueue'], 'readonly');
        const store = transaction.objectStore('offlineQueue');
        const request = store.getAll();
  
        request.onsuccess = () => {
          // Sort by priority and timestamp
          const actions = request.result.sort((a, b) => {
            const priorityOrder = { high: 0, medium: 1, low: 2 };
            // @ts-ignore
            if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
              // @ts-ignore
              return priorityOrder[a.priority] - priorityOrder[b.priority];
            }
            return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
          });
          resolve(actions);
        };
        request.onerror = () => reject(request.error);
      });
    }
  
    async removeFromOfflineQueue(id: number): Promise<void> {
      if (!this.db) throw new Error('Database not initialized');
  
      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction(['offlineQueue'], 'readwrite');
        const store = transaction.objectStore('offlineQueue');
        const request = store.delete(id);
  
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    }
  
    // Utility Methods
    async clearAllData(): Promise<void> {
      if (!this.db) throw new Error('Database not initialized');
  
      const stores = ['timeEntries', 'taskUpdates', 'projects', 'locations', 'offlineQueue'];
      
      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction(stores, 'readwrite');
        
        Promise.all(stores.map(storeName => {
          return new Promise<void>((storeResolve, storeReject) => {
            const store = transaction.objectStore(storeName);
            const request = store.clear();
            request.onsuccess = () => storeResolve();
            request.onerror = () => storeReject(request.error);
          });
        })).then(() => resolve()).catch(reject);
      });
    }
  
    async getStorageUsage(): Promise<{ estimated: number; quota: number }> {
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        const estimate = await navigator.storage.estimate();
        return {
          estimated: estimate.usage || 0,
          quota: estimate.quota || 0
        };
      }
      return { estimated: 0, quota: 0 };
    }
  }
  
  // Export singleton instance
  export const indexedDBService = new IndexedDBService();